import type { BusinessQuizAnswers, ConsumerQuizAnswers, QuizAnswers, ExpressInput, FlowType } from '../types';

type PartialQuizAnswers = Partial<QuizAnswers>;

// ── Goal mapping ────────────────────────────────────────────
const GOAL_TO_PRODUCT_GOAL: Record<string, string> = {
  // Business goals
  cash_flow: 'MANAGE_MULTIPLE_INCOME_SOURCES',
  growth: 'MAXIMIZE_SAVINGS',
  pay_debt: 'PAY_OFF_DEBT',
  tax_planning: 'SAVE_FOR_TAXES',
  save_emergency: 'AUTOMATICALLY_PAY_MYSELF_FIRST',
  // Consumer goals
  invest: 'MAXIMIZE_SAVINGS',
  retirement: 'MAXIMIZE_SAVINGS',
  buy_home: 'MAXIMIZE_SAVINGS',
  budget: 'MANAGE_MULTIPLE_INCOME_SOURCES',
  family: 'AUTOMATICALLY_PAY_MYSELF_FIRST',
  travel: 'MAXIMIZE_SAVINGS',
  other: 'MANAGE_MULTIPLE_INCOME_SOURCES',
};

// ── Industry mapping ────────────────────────────────────────
const INDUSTRY_MAP: Record<string, string> = {
  'Accounting & Finance': 'PROFESSIONAL_SERVICES_AGENCIES',
  'Advertising & Marketing': 'TECHNOLOGY_MEDIA_CREATIVE',
  'Agriculture': 'OTHER',
  'Architecture & Design': 'TECHNOLOGY_MEDIA_CREATIVE',
  'Automotive': 'CONSTRUCTION_SKILLED_TRADES',
  'Banking & Financial Services': 'PROFESSIONAL_SERVICES_AGENCIES',
  'Biotechnology': 'HEALTH_WELLNESS_SOCIAL_SERVICES',
  'Construction': 'CONSTRUCTION_SKILLED_TRADES',
  'Consulting': 'PROFESSIONAL_SERVICES_AGENCIES',
  'E-commerce': 'RETAIL_FOOD_HOSPITALITY',
  'Education': 'HEALTH_WELLNESS_SOCIAL_SERVICES',
  'Energy & Utilities': 'CONSTRUCTION_SKILLED_TRADES',
  'Entertainment & Media': 'TECHNOLOGY_MEDIA_CREATIVE',
  'Fashion & Apparel': 'RETAIL_FOOD_HOSPITALITY',
  'Food & Beverage': 'RETAIL_FOOD_HOSPITALITY',
  'Healthcare': 'HEALTH_WELLNESS_SOCIAL_SERVICES',
  'Hospitality & Tourism': 'RETAIL_FOOD_HOSPITALITY',
  'Information Technology': 'TECHNOLOGY_MEDIA_CREATIVE',
  'Insurance': 'PROFESSIONAL_SERVICES_AGENCIES',
  'Legal Services': 'PROFESSIONAL_SERVICES_AGENCIES',
  'Logistics & Transportation': 'LOGISTICS_TRANSPORTATION_WAREHOUSING',
  'Manufacturing': 'CONSTRUCTION_SKILLED_TRADES',
  'Non-profit': 'HEALTH_WELLNESS_SOCIAL_SERVICES',
  'Pharmaceuticals': 'HEALTH_WELLNESS_SOCIAL_SERVICES',
  'Real Estate': 'PROFESSIONAL_SERVICES_AGENCIES',
  'Retail': 'RETAIL_FOOD_HOSPITALITY',
  'Software & SaaS': 'TECHNOLOGY_MEDIA_CREATIVE',
  'Telecommunications': 'TECHNOLOGY_MEDIA_CREATIVE',
  'Other': 'OTHER',
};

interface BuildSignupUrlParams {
  quizAnswers: PartialQuizAnswers;
  expressInput: ExpressInput;
  flow: FlowType;
  email: string;
}

// ── Rental goal mapping ──────────────────────────────────────
const RENTAL_GOAL_MAP: Record<string, string> = {
  OPTIMIZE: 'MANAGE_MULTIPLE_INCOME_SOURCES',
  PAYDOWN: 'PAY_OFF_DEBT',
  SCALE: 'MAXIMIZE_SAVINGS',
  PASSIVE: 'AUTOMATICALLY_PAY_MYSELF_FIRST',
};

interface BuildRentalSignupUrlParams {
  q1?: string; // landlord type - COM maps to BUSINESS financeAspect
  q3?: string; // primary goal
  email: string;
  utms?: Record<string, string>;
}

export function buildRentalSignupUrl({ q1, q3, email, utms }: BuildRentalSignupUrlParams): string {
  const baseUrl = 'https://app.getsequence.io/signup/';
  const params = new URLSearchParams();

  params.set('financeAspect', q1 === 'COM' ? 'BUSINESS' : 'PERSONAL');

  if (q3) {
    const mapped = RENTAL_GOAL_MAP[q3];
    if (mapped) params.set('mainGoal', mapped);
  }

  params.set('industry', 'PROFESSIONAL_SERVICES_AGENCIES');

  if (email) params.set('email', email);

  params.set('funnel', 'rental');

  if (utms) {
    Object.entries(utms).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
  }

  return `${baseUrl}?${params.toString()}`;
}

// ── Taxes goal mapping ───────────────────────────────────────
const TAXES_Q11_MAP: Record<string, string> = {
  KNOW_WHAT_OWE: 'SAVE_FOR_TAXES',
  NEVER_WORRY: 'SAVE_FOR_TAXES',
  CLEAN_ORGANIZED: 'SAVE_FOR_TAXES',
  FULL_AUTOMATION: 'MANAGE_MULTIPLE_INCOME_SOURCES',
};

interface BuildTaxesSignupUrlParams {
  segment?: string; // Q1 answer - used for financeAspect
  q11?: string;     // goal answer - used for mainGoal
  email: string;
  utms?: Record<string, string>;
}

export function buildTaxesSignupUrl({ segment, q11, email, utms }: BuildTaxesSignupUrlParams): string {
  const baseUrl = 'https://app.getsequence.io/signup/';
  const params = new URLSearchParams();

  // Business full or freelancer -> BUSINESS, others -> PERSONAL
  const isBusinessAspect = segment === 'BUSINESS_FULL' || segment === 'FREELANCER';
  params.set('financeAspect', isBusinessAspect ? 'BUSINESS' : 'PERSONAL');

  if (q11) {
    const mapped = TAXES_Q11_MAP[q11];
    if (mapped) params.set('mainGoal', mapped);
  }

  params.set('industry', 'PROFESSIONAL_SERVICES_AGENCIES');

  if (email) params.set('email', email);

  params.set('funnel', 'taxes');

  if (utms) {
    Object.entries(utms).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
  }

  return `${baseUrl}?${params.toString()}`;
}

// ── Stress quiz goal mapping ─────────────────────────────────────────────────

const STRESS_ARCHETYPE_MAP: Record<string, { financeAspect: string; mainGoal: string }> = {
  juggler:  { financeAspect: 'PERSONAL',  mainGoal: 'MANAGE_MULTIPLE_INCOME_SOURCES' },
  builder:  { financeAspect: 'BUSINESS',  mainGoal: 'SAVE_FOR_TAXES' },
  prisoner: { financeAspect: 'PERSONAL',  mainGoal: 'PAY_OFF_DEBT' },
  avoider:  { financeAspect: 'PERSONAL',  mainGoal: 'AUTOMATICALLY_PAY_MYSELF_FIRST' },
};

interface BuildStressSignupUrlParams {
  archetype: string;
  utms?: Record<string, string>;
}

export function buildStressSignupUrl({ archetype, utms }: BuildStressSignupUrlParams): string {
  const baseUrl = 'https://app.getsequence.io/signup/';
  const params = new URLSearchParams();

  const mapped = STRESS_ARCHETYPE_MAP[archetype];
  if (mapped) {
    params.set('financeAspect', mapped.financeAspect);
    params.set('mainGoal', mapped.mainGoal);
  }

  params.set('funnel', 'stress');

  if (utms) {
    Object.entries(utms).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
  }

  return `${baseUrl}?${params.toString()}`;
}

// ── ADHD quiz type mapping ────────────────────────────────────────────────────

const ADHD_TYPE_MAP: Record<string, { financeAspect: string; mainGoal: string }> = {
  visual:    { financeAspect: 'PERSONAL', mainGoal: 'MANAGE_MULTIPLE_INCOME_SOURCES' },
  autopilot: { financeAspect: 'PERSONAL', mainGoal: 'AUTOMATICALLY_PAY_MYSELF_FIRST' },
  impulsive: { financeAspect: 'PERSONAL', mainGoal: 'MANAGE_MULTIPLE_INCOME_SOURCES' },
  burnedout: { financeAspect: 'PERSONAL', mainGoal: 'AUTOMATICALLY_PAY_MYSELF_FIRST' },
};

interface BuildAdhdSignupUrlParams {
  type: string;
  utms?: Record<string, string>;
}

export function buildAdhdSignupUrl({ type, utms }: BuildAdhdSignupUrlParams): string {
  const baseUrl = 'https://app.getsequence.io/signup/';
  const params = new URLSearchParams();

  const mapped = ADHD_TYPE_MAP[type];
  if (mapped) {
    params.set('financeAspect', mapped.financeAspect);
    params.set('mainGoal', mapped.mainGoal);
  }

  params.set('funnel', 'adhd');

  if (utms) {
    Object.entries(utms).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
  }

  return `${baseUrl}?${params.toString()}`;
}

// ── Wellbeing quiz mapping ──────────────────────────────────────────────────

const WELLBEING_GOAL_MAP: Record<string, string> = {
  LESS_STRESS: 'AUTOMATICALLY_PAY_MYSELF_FIRST',
  SAVE_MORE: 'MAXIMIZE_SAVINGS',
  PAY_DEBT: 'PAY_OFF_DEBT',
  ORGANIZE: 'MANAGE_MULTIPLE_INCOME_SOURCES',
};

interface BuildWellbeingSignupUrlParams {
  segment?: string;
  goal?: string;
  email: string;
  utms?: Record<string, string>;
}

export function buildWellbeingSignupUrl({ segment, goal, email, utms }: BuildWellbeingSignupUrlParams): string {
  const baseUrl = 'https://app.getsequence.io/signup/';
  const params = new URLSearchParams();

  const isBusinessAspect = segment === 'BUSINESS_FULL' || segment === 'FREELANCER';
  params.set('financeAspect', isBusinessAspect ? 'BUSINESS' : 'PERSONAL');

  if (goal) {
    const mapped = WELLBEING_GOAL_MAP[goal];
    if (mapped) params.set('mainGoal', mapped);
  }

  params.set('industry', 'PROFESSIONAL_SERVICES_AGENCIES');

  if (email) params.set('email', email);

  params.set('funnel', 'wellbeing');

  if (utms) {
    Object.entries(utms).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
  }

  return `${baseUrl}?${params.toString()}`;
}

export function buildSignupUrl({ quizAnswers, expressInput, flow, email }: BuildSignupUrlParams): string {
  const baseUrl = 'https://app.getsequence.io/signup/';
  const params = new URLSearchParams();

  // financeAspect
  const userType = quizAnswers?.userType || expressInput.userType;
  params.set('financeAspect', userType === 'BUSINESS' ? 'BUSINESS' : 'PERSONAL');

  // mainGoal
  const answers = quizAnswers as Partial<BusinessQuizAnswers | ConsumerQuizAnswers> | undefined;
  const goals = answers?.goals;
  if (goals && goals.length > 0) {
    const mapped = GOAL_TO_PRODUCT_GOAL[goals[0]];
    if (mapped) params.set('mainGoal', mapped);
  }

  // industry (business only)
  if (userType === 'BUSINESS') {
    const industry = (quizAnswers as Partial<BusinessQuizAnswers>)?.industry;
    if (industry) {
      const mapped = INDUSTRY_MAP[industry];
      if (mapped) params.set('industry', mapped);
    }
  }

  // email
  if (email) {
    params.set('email', email);
  }

  // funnel (always last)
  params.set('funnel', flow === 'express' ? 'express' : 'quiz');

  return `${baseUrl}?${params.toString()}`;
}
